import _ from 'lodash'
import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'

import WarningIconObject from '@glass-designer/objects/warningIconObject'
import GuidelineWarning from '@glass-designer/model/guidelineWarning'
import CircleHole from '@glass-designer/model/holes/circleHole'
import RectangleHole from '@glass-designer/model/holes/rectangleHole'

import GlassDesignerTypes from '@glass-designer/types'
import { GlassTypes, OrderEditorGuidelineType, OrderEditorCutoutTypes, AccountSettingKeys } from '@/store/types'
import { SegmentTypes } from '@/store/mathTypes'


/**
 * Guideline Helper
 * Handles the inspection of the glass shapes in the editor & checks for guideline errors / warnings
 * Adds issues to the inspect list and maintains the list of warning icons in the scene
 */
export default class GuidelineHelper {
  constructor(editor) {
    this.editor = editor

    // Guideline Config
    this.minCornerAngleThreshold = 135    // Any corner between 2 lines less then this is considered a corner

    // Warning Icon pool
    // Used to reuse icon objects (and their WebGL resources)
    this.iconObjectPool = []

    this.warningList = []
  }


  /**
   * Get the list of warnings in the current order
   */
  getWarningList() {
    return this.warningList
  }


  /**
   * Get a guideline warning given an id
   * Returns null if not found
   */
  getWarningById(id) {
    return this.warningList.find(warning => warning.id == id)
  }


  /**
   * Get guideline warnings given a shape id
   */
  getWarningsByShapeId(shapeId) {
    return this.warningList.filter(warning => warning.glassShapeId == shapeId)
  }


  /**
   * Reset & release all icon objects
   */
  resetAllIconObjects() {
    let iconList = this.editor.getObjectListOfType([WarningIconObject])
    this.releaseIconObject(iconList)
  }


  /**
   * Release a single (or array of icons) back into the icon pool for reusing
   */
  releaseIconObject(icon) {
    if(_.isArray(icon)) {
      icon.forEach(listIcon => {
        this.releaseIconObject(listIcon)
      })
    } else {

      // Ignore if already in the pool
      if(this.iconObjectPool.find(poolIcon => poolIcon.id == icon.id) != null)
        return

      // Clear settings
      icon.setVisible(false)
      icon.isReleasedWarning = true
      
      this.iconObjectPool.push(icon)
    }
  }


  /**
   * Update view level
   */
  viewLevelUpdated(viewLevel) {
    this.displayWarningsOptionUpdated()
  }


  /**
   * Warning display option updated
   */
  displayWarningsOptionUpdated() {
    var warningsVisible = this.isWarningsVisible()

    let iconList = this.editor.getObjectListOfType([WarningIconObject])
    iconList.forEach(iconObj => {
      // Ignore if released into the pool
      if(iconObj.isReleasedWarning)
        return

      iconObj.setVisible(warningsVisible)
    })
  }


  /**
   * Check if warnings are visible
   * Based on display warnings option & current view level
   */
  isWarningsVisible() {
    if(!this.editor.getOptionDisplayWarnings())
      return false
    if(this.editor.getViewLevel() != GlassDesignerTypes.EditorViewLevelTypes.normal)
      return false
    return true
  }


  /**
   * Run a guideline inspection of all shapes in the editor
   */
  runFullGuidelineInspection() {
    this.resetAllIconObjects()
    this.warningList = []

    this.editor.getValidShapeList().forEach(shape => {
      this.runGuidelineInspectionOnShape(shape)
    })
  }


  /**
   * Run a guideline inspection on a glass shape in the editor
   */
  runGuidelineInspectionOnShape(glassShape) {
    let guidelineList = this.editor.getEditorConfigurationData().orderEditorGuidelines

    let shapeThickness = glassShape.getThickness()
    let shapeGlassType = glassShape.getGlassType()

    // Convert the corner radiuses of the shape into arcs
    let shapeConvertedSegList = MathsUtils.convertSegmentListCornerRadiusToArcs(glassShape.segmentList, false)

    // Filter guidelines for the shape thickness
    let shapeGuideline = guidelineList.find(guideline => {
      if(guideline.glassType != GlassTypes.all) {
        if(guideline.glassType != shapeGlassType)
          return false
      }
      if(shapeThickness >= guideline.minThickness && shapeThickness < guideline.maxThickness)
        return true
      return false
    })

    // Release icons associated with the shape
    let iconList = this.editor.getObjectListOfType([WarningIconObject])
    iconList.forEach(curIcon => {
      if(curIcon.userData && curIcon.userData.glassShapeId == glassShape.id) {
        this.releaseIconObject(curIcon)
      }
    })

    // Clear warnings associated with this shape
    this.warningList = this.warningList.filter(warning => {
      return warning.glassShapeId != glassShape.id
    })

    var shapeWarningList = []

    if(shapeGuideline) {
      // Center Cutout to Center Cutout guidelines
      let centerCutoutList = glassShape.getCenterCutoutList()
      for(var i=0; i<centerCutoutList.length; i++) {
        let centerCutoutBounds = centerCutoutList[i].calculateSegmentBounds()
        let centerCutoutConvertedSegs = MathsUtils.convertSegmentListCornerRadiusToArcs(centerCutoutList[i].segmentList)

        for(var j=i+1; j<centerCutoutList.length; j++) {
          shapeWarningList = shapeWarningList.concat(this._inspectCenterCutoutToCenterCutout(glassShape, shapeGuideline, centerCutoutList[i], centerCutoutConvertedSegs, centerCutoutBounds, centerCutoutList[j]))
        }

        // Center Cutout to Panel Edge/Corner guidelines
        shapeWarningList = shapeWarningList.concat(this._inspectCenterCutoutToPanel(glassShape, shapeConvertedSegList, shapeGuideline, centerCutoutList[i], centerCutoutConvertedSegs, centerCutoutBounds))

        // Center Cutout - Corner Radius guidelines
        shapeWarningList = shapeWarningList.concat(this._inspectCenterCutoutCornerRadius(glassShape, shapeGuideline, centerCutoutList[i], centerCutoutBounds))
      }

      // Glass Shape - Corner Radius guidelines
      shapeWarningList = shapeWarningList.concat(this._inspectGlassShapeCornerRadius(glassShape, shapeGuideline))
    }

    // Glass Shape - Split guidelines
    shapeWarningList = shapeWarningList.concat(this._inspectGlassShapeSplits(glassShape))



    // Add icons
    shapeWarningList.forEach(warning => {
      let warningIcon = this.createWarningIcon()
      warningIcon.setPosition(warning.displayPoint)
      warningIcon.guidelineWarningId = warning.id
      warningIcon.warningTitle = warning.getName()
      warningIcon.warningHTMLDescription = warning.toHTMLString()
      warningIcon.warningFixAvailable = warning.fixable
      warningIcon.userData = {
        glassShapeId: glassShape.id
      }
    })

    this.warningList = this.warningList.concat(shapeWarningList)
  }


  /**
   * Inspect the guidelines with a center cutout in the shape to another center cutout in the same shape
   * Returns a list of GuidelineWarning objects
   */
  _inspectCenterCutoutToCenterCutout(glassShape, shapeGuideline, centerCutout, centerCutoutSegmentList, centerCutoutBounds, otherCenterCutout) {
    if(!shapeGuideline)
      return []

    var warningList = []

    var cutoutMinDist = -1

    let otherCutoutBounds = otherCenterCutout.calculateSegmentBounds()
    let otherCutoutConvertedSegs = MathsUtils.convertSegmentListCornerRadiusToArcs(otherCenterCutout.segmentList)

    // If both cutouts are circles, run a faster circle-circle check
    if(centerCutout instanceof CircleHole && otherCenterCutout instanceof CircleHole) {
      cutoutMinDist = centerCutoutBounds.getCenter(new THREE.Vector2()).distanceTo(otherCutoutBounds.getCenter(new THREE.Vector2()))
      cutoutMinDist -= centerCutout.radius
      cutoutMinDist -= otherCenterCutout.radius

    } else {

      // Simple bounds check first to avoid expensive segment list checking
      cutoutMinDist = MathsUtils.calculateBox2Box2MinDistance(centerCutoutBounds, otherCutoutBounds)
      if(cutoutMinDist >= shapeGuideline.minCenterEdgeToEdgeClearance)
        return []

      // More precise segment to segment check
      let cutoutClosestPointInfo = this.calculateSegmentListMinDistanceToSegmentList(centerCutoutSegmentList, otherCutoutConvertedSegs)
      if(cutoutClosestPointInfo.closestEdgeInfo)
        cutoutMinDist = cutoutClosestPointInfo.closestEdgeInfo.distance
    }

    if(cutoutMinDist >= 0 && cutoutMinDist < shapeGuideline.minCenterEdgeToEdgeClearance) {

      // Add the warning
      warningList.push(new GuidelineWarning({
        type: OrderEditorGuidelineType.centerCutoutEdgeToEdge,
        glassShapeId: glassShape.id,
        centerCutoutId: centerCutout.id,
        position: centerCutoutBounds.getCenter(new THREE.Vector2()),
        displayPoint: centerCutoutBounds.getCenter(new THREE.Vector2()),
        clearanceRequired: shapeGuideline.minCenterEdgeToEdgeClearance,
        clearance: cutoutMinDist,
        shapeThickness: glassShape.getThickness()
      }))
    }
  
    return warningList
  }


  /**
   * Inspect the guidelines with a center cutout in the shape to the glass edges/corners
   * Returns a list of GuidelineWarning objects
   */
  _inspectCenterCutoutToPanel(glassShape, shapeConvertedSegList, shapeGuideline, centerCutout, centerCutoutSegmentList, centerCutoutBounds) {
    if(!shapeGuideline)
      return []

    var warningList = []

    // Check minimum distance from segments of the cutout to the segments of the glass panel
    let closestPointInfo = this.calculateSegmentListMinDistanceToSegmentList(centerCutoutSegmentList, shapeConvertedSegList)

    // Corner check
    if(closestPointInfo.closestCornerInfo) {
      if(closestPointInfo.closestCornerInfo.distance < shapeGuideline.minCenterCornerClearance) {        

        // Add the warning
        warningList.push(new GuidelineWarning({
          type: OrderEditorGuidelineType.centerCutoutEdgeToPanelCorner,
          glassShapeId: glassShape.id,
          centerCutoutId: centerCutout.id,
          position: centerCutoutBounds.getCenter(new THREE.Vector2()),
          displayPoint: centerCutoutBounds.getCenter(new THREE.Vector2()),
          clearanceRequired: shapeGuideline.minCenterCornerClearance,
          clearance: closestPointInfo.closestCornerInfo.distance,
          shapeThickness: glassShape.getThickness()
        }))

        return warningList
      }
    }

    // Edge check
    if(closestPointInfo.closestEdgeInfo) {
      if(closestPointInfo.closestEdgeInfo.distance < shapeGuideline.minCenterEdgeClearance) {        

        warningList.push(new GuidelineWarning({
          type: OrderEditorGuidelineType.centerCutoutEdgeToPanelEdge,
          glassShapeId: glassShape.id,
          centerCutoutId: centerCutout.id,
          position: centerCutoutBounds.getCenter(new THREE.Vector2()),
          displayPoint: centerCutoutBounds.getCenter(new THREE.Vector2()),
          clearanceRequired: shapeGuideline.minCenterEdgeClearance,
          clearance: closestPointInfo.closestEdgeInfo.distance,
          shapeThickness: glassShape.getThickness()
        }))

        return warningList
      }
    }
    
    return warningList
  }


  /**
   * Inspect the corner radiuses of a center cutout
   * Returns a list of GuidelineWarning objects
   */
  _inspectCenterCutoutCornerRadius(glassShape, shapeGuideline, centerCutout, centerCutoutBounds) {
    var testList = []
    var warningList = []

    // If a circle, do a faster check
    // A complete circle has 100% arc percent
    if(centerCutout instanceof CircleHole) {
      testList = [
        { arcPercent: 100, radius: centerCutout.radius }
      ]

    } else {
      // Go through all corner radiuses / arcs

      // Go through arcs / corner radiuses
      centerCutout.segmentList.forEach((segment, segmentIndex) => {
        let prevSegment = centerCutout.segmentList[segmentIndex == 0 ? centerCutout.segmentList.length-1 : segmentIndex-1]

        if(segment.type == SegmentTypes.arc) {
          let arcAngleDistance = segment.calculateArcAngleDistance()
          let arcPercent = (Math.abs(arcAngleDistance) / 360.0) * 100.0

          testList.push({
            arcPercent: arcPercent, radius: segment.arcRadius
          })

        } else {

          // Rounded corners
          if(segment.startCornerRadius > 0) {
            let roundingInfo = MathsUtils.calculateSegmentCornerRounding(prevSegment, segment, true)
            if(roundingInfo) {
              testList.push({
                arcPercent: Math.abs(roundingInfo.arcPercent * 100), radius: roundingInfo.radius
              })
            }     
          // Sharp corners (radius = 0)       
          } else {
            let cornerInfo = MathsUtils.calculateSegmentNonRoundedCornerDetails(prevSegment, segment, true)
            testList.push({
              arcPercent: Math.abs(cornerInfo.arcPercent * 100), radius: 0
            })
          }
        }
      })
    }

    // Go through all tests
    testList.forEach(arcTest => {
      // Only use 1 arc radius warning per cutout
      if(warningList.length > 0)
        return

      var requiredMinRadius = shapeGuideline.minRadiusAbovePercent
      if(arcTest.arcPercent < shapeGuideline.cornerRadiusTestPercent)
        requiredMinRadius = shapeGuideline.minRadiusBelowPercent
  
      if(arcTest.radius < requiredMinRadius) {
        warningList.push(new GuidelineWarning({
          type: OrderEditorGuidelineType.centerCutoutRadius,
          glassShapeId: glassShape.id,
          centerCutoutId: centerCutout.id,
          position: centerCutoutBounds.getCenter(new THREE.Vector2()),
          displayPoint: centerCutoutBounds.getCenter(new THREE.Vector2()),
          radiusArcPercent: arcTest.arcPercent,
          radiusArcTestPercent: shapeGuideline.cornerRadiusTestPercent,
          radiusRequired: requiredMinRadius,
          radius: arcTest.radius,
          shapeThickness: glassShape.getThickness(),
          fixable: true
        }))
  
      }      
    })

    return warningList
  }


  /**
   * Inspect the corner radiuses of the glass shape
   * Returns a list of GuidelineWarning objects
   */
  _inspectGlassShapeCornerRadius(glassShape, shapeGuideline) {
    let cutoutList = glassShape.calculateCNCCutoutList()
    var warningList = []

    // Notes
    // Will use the 'pathSegmentList' property of cutouts as that has start/end corner radiuses converted to arcs (as it knows prev/next segments)
    // Only handle corner / edge cutouts. Center cutouts are handled by GuidelineHelper._inspectCenterCutoutCornerRadius()
    // Assumes the panel is clockwise winding, so will only inspect arcs with counter-clockwise winding
    // This is done so we only test arcs/corners that are cut into the glass
    // Corners/Arcs on the convex hull do not have the same restrictions
    cutoutList.forEach(cutout => {
      if(cutout.type != OrderEditorCutoutTypes.cornerCutOut && cutout.type != OrderEditorCutoutTypes.edgeCutOut)
        return

      cutout.pathSegmentList.forEach((pathSegment, index) => {
        var arcPercent = 0
        var arcRadius = 0
        var position = new THREE.Vector2()
        var displayPoint = new THREE.Vector2()
        var prevPathSegment = index > 0 ? cutout.pathSegmentList[index-1] : null
        var segmentId = null

        // Arcs
        if(pathSegment.type == SegmentTypes.arc) {
          // Ignore clockwise arcs
          if(pathSegment.arcClockwise)
            return

          let arcAngleDistance = pathSegment.calculateArcAngleDistance()
          arcPercent = (Math.abs(arcAngleDistance) / 360.0) * 100.0
          arcRadius = pathSegment.arcRadius
          position.copy(pathSegment.startPosition)
          displayPoint.copy(pathSegment.getMidpoint())
        }

        // Start corner radius
        if(pathSegment.type == SegmentTypes.line) {
          if(index == 0)
            return

          // Ignore Arc -> Line connection
          if(prevPathSegment && prevPathSegment.type == SegmentTypes.arc)
            return

          // Find segment id in the cutout that matches the segment in the shape
          let glassSegment = glassShape.segmentList.find(shapeSeg => {
            if(shapeSeg.type == SegmentTypes.line) {
              if(shapeSeg.startPosition.distanceTo(pathSegment.startPosition) < 0.01)
                return true
            }
            return false
          })

          segmentId = glassSegment ? glassSegment.id : null

          // TODO: Test for CCW only

          // Figure out angles of 2 segments without corner radius
          if(pathSegment.startCornerRadius == 0) {
            let cornerInfo = MathsUtils.calculateSegmentNonRoundedCornerDetails(prevPathSegment, pathSegment, false)
            arcPercent = cornerInfo.arcPercent * 100.0
            arcRadius = 0
            position.copy(pathSegment.startPosition)
            displayPoint.copy(prevPathSegment.endPosition)
          } else {
            // Rounded corner
            let roundingInfo = MathsUtils.calculateSegmentCornerRounding(prevPathSegment, pathSegment, false)
            if(roundingInfo) {
              arcPercent = roundingInfo.arcPercent * 100.0
              arcRadius = roundingInfo.radius
              position.copy(pathSegment.startPosition)
              displayPoint.copy(roundingInfo.midpoint)
            }
            else {
              return
            }
          }
        }

        var requiredMinRadius = shapeGuideline.minRadiusAbovePercent
        if(arcPercent < shapeGuideline.cornerRadiusTestPercent)
          requiredMinRadius = shapeGuideline.minRadiusBelowPercent
    
        if(arcRadius < requiredMinRadius) {
          warningList.push(new GuidelineWarning({
            type: OrderEditorGuidelineType.panelCornerRadius,
            glassShapeId: glassShape.id,
            segmentId: segmentId,
            position: position,      // Note: Important the point matches exactly at the segment start for the fixer to find the segment
            displayPoint: displayPoint,
            radiusArcPercent: arcPercent,
            radiusArcTestPercent: shapeGuideline.cornerRadiusTestPercent,
            radiusRequired: requiredMinRadius,
            radius: arcRadius,
            shapeThickness: glassShape.getThickness(),
            fixable: pathSegment.type == SegmentTypes.line    // Only really need to fix corner radiuses
          }))
        }    
      })
      
    })

    return warningList
  }


  /**
   * Inspect the splits of the glass shape
   * Returns a list of GuidelineWarning objects
   */
  _inspectGlassShapeSplits(glassShape) {
    var warningList = []

    let isGlassAnnealed = glassShape.getGlassType() == GlassTypes.annealed
    let shapeSplitCount = glassShape.getSplitLineList().length
    let annealedSplitLimit = parseInt(this.editor.getEditorConfigurationData().orderEditorSettings[AccountSettingKeys.annealedSplittingMaxSplits] || 0)
    let laminateSplitLimit = parseInt(this.editor.getEditorConfigurationData().orderEditorSettings[AccountSettingKeys.laminateSplittingMaxSplits] || 0)
    let splitLimit = isGlassAnnealed ? annealedSplitLimit : laminateSplitLimit

    if(shapeSplitCount > 0 && shapeSplitCount > splitLimit) {
      warningList.push(new GuidelineWarning({
        type: OrderEditorGuidelineType.splitLimit,
        glassShapeId: glassShape.id,
        splitCount: shapeSplitCount,
        splitLimit: splitLimit,
        position: glassShape.calculateShapeMidpoint(),
        displayPoint: glassShape.calculateShapeMidpoint(),
        shapeThickness: glassShape.getThickness(),
        fixable: false
      }))
    }

    return warningList
  }


  /**
   * Create a new warning icon object
   * Attempts to reuse an existing icon released into the object pool
   * Otherwise creates a new icon
   */
  createWarningIcon(glassShape) {
    var existingObjIndex = this.iconObjectPool.findIndex(poolObj => {
      return (poolObj instanceof WarningIconObject)
    })

    var warningsVisible = this.isWarningsVisible()

    if(existingObjIndex != -1) {
      // Remove from the pool & setup for reuse
      var existingObj = this.iconObjectPool[existingObjIndex]     
      this.iconObjectPool.splice(existingObjIndex, 1)

      existingObj.setVisible(warningsVisible)
      existingObj.isReleasedWarning = false

      return existingObj
    }

    // Create a new warning icon
    var newIconObject = new WarningIconObject(this.editor)
    newIconObject.isReleasedWarning = false
    newIconObject.setVisible(warningsVisible)

    this.editor.scene.add(newIconObject.getSceneObject())

    return newIconObject
  }


  /**
   * Fix a guideline warning automatically
   * Only some guideline warnings (like min corner radius) can be fixed automatically
   * 
   */
  fixGuidelineWarning(guidelingWarningId) {
    let warningObj = this.warningList.find(warning => warning.id == guidelingWarningId)
    if(!warningObj)
      return
    if(!warningObj.fixable)
      return

    let glassShape = this.editor.getObjectById(warningObj.glassShapeId)
    if(!glassShape)
      return

    this.editor.startUndoGroup('Fixing guideline warning')
    this.editor.addUndoShape(glassShape)

    switch(warningObj.type) {
      case OrderEditorGuidelineType.centerCutoutRadius:
        {
          let centerCutout = glassShape.getHoleById(warningObj.centerCutoutId)
          if(centerCutout) {
            if(centerCutout instanceof CircleHole)
              centerCutout.radius = warningObj.radiusRequired
            if(centerCutout instanceof RectangleHole)
              centerCutout.setCornerRadius(warningObj.radiusRequired)
            
            centerCutout.holeSizeUpdated()
            centerCutout.rebuildHoleShape()
          }
        }        
        break

      case OrderEditorGuidelineType.panelCornerRadius:
        // Find the line segment & update start corner radius
        // Don't currently apply to arcs
        {
          let segment = glassShape.getSegmentById(warningObj.segmentId)
          if(segment) {
            let segmentIndex = glassShape.findSegmentIndexById(warningObj.segmentId)

            glassShape.updateSegmentStartCornerRadius(segmentIndex, warningObj.radiusRequired)
            glassShape.segmentListUpdated()
            glassShape.updateSceneObject()
          }
        }
        break
    }

    this.editor.closeCurrentUndoGroup()

    // Flag shape updated
    // Will re-run all guideline checks, shape rebuild & render updates
    this.editor.onShapeUpdated(glassShape)
  }


  /**
   * Calculate the minimum distance between two segment lists
   * Finds the closest point to each other along both lists and returns the distance
   * sourceSegmentList should be the center cutout segment list as it has less precision on tests
   * 
   * Returns an object containing the details about the closest points between the two segment lists
   * Returns null if no closest point was found
   */
  calculateSegmentListMinDistanceToSegmentList(sourceSegmentList, compareSegmentList) {
    var closestEdgeInfo = {
      closestDistSq: -1,
      point1: null,
      point2: null,
      closestSegmentIndex1: -1,
      closestSegmentIndex2: -1
    }
    var closestCornerInfo = {
      closestDistSq: -1,
      point1: null,
      point2: null,
      closestSegmentIndex1: -1,
      closestSegmentIndex2: -1
    }

    // Note: Uses distance squared for speed
    // Closest 2 points will use correct sqrt for final distance
    sourceSegmentList.forEach((segment1, segmentIndex1) => {
      // Check few points along the segment line/arc
      // Note: Not super precise & not optimised
      var segPoints = [
        segment1.getPoint(0),
        segment1.getPoint(0.25),
        segment1.getPoint(0.5),
        segment1.getPoint(0.75),
        segment1.getPoint(1),
      ]

      compareSegmentList.forEach((segment2, segmentIndex2) => {
        let prevSeg2 = compareSegmentList[segmentIndex2 == 0 ? compareSegmentList.length-1 : segmentIndex2-1]
        
        segPoints.forEach(segPoint => {
          let projectedPoint = segment2.projectPointOnSegment(segPoint, true)
          let dist = projectedPoint.distanceToSquared(segPoint)

          if(closestEdgeInfo.closestDistSq < 0 || dist < closestEdgeInfo.closestDistSq) {
            closestEdgeInfo.closestDistSq = dist
            closestEdgeInfo.point1 = segPoint
            closestEdgeInfo.point2 = projectedPoint

            closestEdgeInfo.closestSegmentIndex1 = segmentIndex1
            closestEdgeInfo.closestSegmentIndex2 = segmentIndex2
          }

          // Only need to check start corner. End position will be checked next segment
          let cnr = segment2.startPosition.clone()

          // Get angle between this line & previous line to calc angle
          // If angle is too great, don't treat it as a corner
          if(segment2.type == SegmentTypes.line) {
            if(prevSeg2.type == SegmentTypes.line) {
              let cornerAngle = MathsUtils.calculateSegmentAngleDistance(prevSeg2, segment2)
              
              // Angle too large? Not a corner
              if(cornerAngle > this.minCornerAngleThreshold)
                return
            }
          }

          // If an arc & converted from a corner radius, use the closest point on the arc as the corner
          if(segment2.type == SegmentTypes.arc && segment2.isArcFromCornerRadius)
            cnr = segment2.projectPointOnSegment(segPoint, true)
            
          
          dist = cnr.distanceToSquared(segPoint)
          if(closestCornerInfo.closestDistSq < 0 || dist < closestCornerInfo.closestDistSq) {
            closestCornerInfo.closestDistSq = dist
            closestCornerInfo.point1 = segPoint
            closestCornerInfo.point2 = cnr

            closestCornerInfo.closestSegmentIndex1 = segmentIndex1
            closestCornerInfo.closestSegmentIndex2 = segmentIndex2
          }
        })
      })
    })

    if(closestCornerInfo.point1 && closestCornerInfo.point2)
      closestCornerInfo.distance = closestCornerInfo.point1.distanceTo(closestCornerInfo.point2)
    else
      closestCornerInfo = null

    if(closestEdgeInfo.point1 && closestEdgeInfo.point2)
      closestEdgeInfo.distance = closestEdgeInfo.point1.distanceTo(closestEdgeInfo.point2)
    else
      closestEdgeInfo = null

    return {
      closestCornerInfo: closestCornerInfo,
      closestEdgeInfo: closestEdgeInfo
    }
  }


  /**
   * Calculate the minimum corner radius given a glass type, thickness & arc percent
   * Defaults to 25%, used by 90degree corners
   */
  calculateMinimumCornerRadiusForThickness(glassType, thickness, arcPercent = 25) {
    let guidelineList = this.editor.getEditorConfigurationData().orderEditorGuidelines

    let shapeGuideline = guidelineList.find(guideline => {
      if(guideline.glassType != GlassTypes.all) {
        if(guideline.glassType != glassType)
          return false
      }

      if(thickness >= guideline.minThickness && thickness < guideline.maxThickness)
        return true
      return false
    })

    if(!shapeGuideline)
      return 0

    if(arcPercent < shapeGuideline.cornerRadiusTestPercent)
      return shapeGuideline.minRadiusBelowPercent

    return shapeGuideline.minRadiusAbovePercent
  }



}

